'use client'

import dynamic from 'next/dynamic'
import { Callout, Figure, Goal, H, Hl, Lesson, P, Quiz, Step } from '../_components/lesson'

const ExampleLearner = dynamic(() => import('../_components/widgets/ExampleLearner'), { ssr: false })

function KindCard({ title, tag, children, color }: { title: string; tag: string; children: React.ReactNode; color: string }) {
  return (
    <div className="gd-card p-5">
      <span className="gd-label !text-[10px]" style={{ color }}>
        {tag}
      </span>
      <h3 className="gd-serif mt-1 text-[26px] leading-tight">{title}</h3>
      <p className="mt-2 text-[15px] leading-relaxed" style={{ color: 'var(--gd-ink-2)' }}>
        {children}
      </p>
    </div>
  )
}

export default function WhatIsMl() {
  return (
    <Lesson>
      <Step>
        <H>Could you write the rules?</H>
        <P>
          You can tell a cat from a dog in a fraction of a second. But try writing down the <em>rules</em> you use. Pointy ears?
          Some dogs have them. Size? Some cats are huge. Whiskers? Both. Every rule you write breaks on some animal.
        </P>
        <P>
          For decades, that’s how people tried to make computers smart: by writing rules. It works for chess openings and tax
          forms, but it falls apart for anything messy — faces, voices, language, the real world.
        </P>
        <P>
          <strong>Machine learning flips the approach.</strong> Instead of writing the rules, you show the computer lots of{' '}
          <Hl c="blue">examples</Hl> with the right <Hl c="amber">answers</Hl>, and it works out the rules itself.
        </P>
        <Callout kind="plain">
          Traditional programming: <em>rules + data → answers</em>. Machine learning: <em>data + answers → rules</em>.
        </Callout>
      </Step>

      <Step gate="spam-learned" next="What just happened?">
        <H>Teach a machine, right now</H>
        <P>
          Below is a real (tiny) learning machine. Each dot is an email, placed by two measurements: how many links it contains,
          and how many exclamation marks. <Hl c="blue">Blue circles</Hl> are normal emails, <Hl c="amber">amber squares</Hl> are
          spam.
        </P>
        <P>
          The shading is the machine’s current opinion of every possible email. Watch it learn, then drag the glowing{' '}
          <Hl c="accent">mystery email</Hl> around to see what it would decide.
        </P>
        <Goal id="spam-learned" task={<>Add a few more examples of your own (at least 10 in total) until the machine makes zero mistakes.</>}>
          {onReach => (
            <Figure n="0.1" caption="A spam filter that learns from your examples. Tap to add an email; drag the mystery email to query the model.">
              <ExampleLearner onLearned={onReach} />
            </Figure>
          )}
        </Goal>
      </Step>

      <Step gate="q-learning">
        <H>So what did it actually learn?</H>
        <P>
          Look at the readouts under the chart. The entire “brain” of this machine is three numbers: two{' '}
          <Hl c="green">weights</Hl> and a <Hl c="green">bias</Hl>. That’s it.
        </P>
        <P>
          When it was “learning”, it was doing something almost embarrassingly simple: <strong>nudging those three numbers, a
          tiny bit at a time, in whatever direction made its mistakes smaller.</strong> The pink <Hl c="pink">loss</Hl> number
          measures how wrong it is overall — and learning is just making that number go down.
        </P>
        <P>
          Press <em>Forget everything</em> and watch: the numbers reset to zero, the machine knows nothing, and then the nudging
          starts again.
        </P>
        <Quiz
          id="q-learning"
          question="When this machine “learns”, what is really happening?"
          options={[
            { label: 'It memorises every example so it can look them up later.', why: 'Its whole memory is just three numbers — far too small to store every example.' },
            { label: 'A programmer writes new rules whenever it makes a mistake.', why: 'Nobody touched the rules. The computer adjusted itself.' },
            { label: 'It adjusts a few numbers, step by step, to make its mistakes smaller.', correct: true },
            { label: 'It understands what spam means, like a person would.', why: 'It has no idea what an email is. It found a pattern in two measurements.' },
          ]}
          explain="Every model in this course — up to the giant language models — learns this way: some numbers (parameters), a measure of wrongness (loss), and many small nudges that shrink it."
        />
      </Step>

      <Step>
        <H>The same idea, scaled up</H>
        <P>
          Your spam filter had 3 numbers. The language models behind modern AI assistants have hundreds of <em>billions</em>.
          Their inputs aren’t two measurements but entire passages of text. But the loop is the same one you just watched:
        </P>
        <Figure n="0.2" caption="The learning loop. Everything in machine learning is a variation on these four steps." paper>
          <div className="grid gap-3 py-2 sm:grid-cols-4">
            {[
              ['1', 'Predict', 'Run the model on an example.', 'var(--gd-blue)'],
              ['2', 'Measure', 'Compare with the right answer → loss.', 'var(--gd-pink)'],
              ['3', 'Nudge', 'Adjust every parameter to shrink the loss.', 'var(--gd-green)'],
              ['4', 'Repeat', 'Millions of times, on lots of examples.', 'var(--gd-accent)'],
            ].map(([n, t, d, col]) => (
              <div key={n} className="rounded-[10px] border p-4" style={{ borderColor: 'var(--gd-rule)', background: 'var(--gd-surface)' }}>
                <span className="gd-serif text-[34px] leading-none" style={{ color: col }}>
                  {n}
                </span>
                <p className="mt-2 font-semibold">{t}</p>
                <p className="mt-1 text-[14px] leading-snug" style={{ color: 'var(--gd-ink-2)' }}>
                  {d}
                </p>
              </div>
            ))}
          </div>
        </Figure>
        <P>
          By the end of this course you will have written every one of these steps yourself — first for a straight line, then a
          neural network, then a small GPT-style language model that trains in your browser.
        </P>
      </Step>

      <Step gate="q-kinds">
        <H>Three ways to learn</H>
        <P>Machine learning comes in a few flavours, depending on what kind of examples you have:</P>
        <div className="my-6 grid gap-3">
          <KindCard title="Supervised learning" tag="examples + answers" color="var(--gd-blue)">
            Every example comes with the right answer, like your labelled emails. The model learns to predict the answer for new
            examples. (Spam filters, image recognition, medical diagnosis.)
          </KindCard>
          <KindCard title="Unsupervised learning" tag="examples only" color="var(--gd-violet)">
            No answers at all. The model finds structure by itself — groups of similar customers, unusual transactions, topics in
            a pile of documents.
          </KindCard>
          <KindCard title="Reinforcement learning" tag="trial, error, reward" color="var(--gd-green)">
            No examples — just actions and rewards. The model tries things and learns which ones pay off. (Game-playing AIs,
            robots learning to walk.)
          </KindCard>
        </div>
        <Callout kind="why">
          Large language models use a clever twist called <strong>self-supervised learning</strong>: the “answer” for every bit of
          text is simply the next word. The internet provides trillions of free examples — no humans needed to label them.
        </Callout>
        <Quiz
          id="q-kinds"
          question="You give a model 10,000 photos of skin moles, each labelled “harmless” or “see a doctor” by dermatologists. What kind of learning is this?"
          options={[
            { label: 'Supervised', correct: true },
            { label: 'Unsupervised', why: 'Every photo comes with an answer from a doctor — that’s the defining feature of supervised learning.' },
            { label: 'Reinforcement', why: 'There are no actions or rewards here, just examples with answers.' },
          ]}
          explain="Examples plus the right answers = supervised learning. It’s the most common kind, and where we’ll start."
        />
      </Step>

      <Step>
        <H>How this course works</H>
        <P>
          You don’t need to know any math or programming to start. We build everything from the ground up, and every idea comes
          with something to poke at:
        </P>
        <ul className="gd-list">
          <li>
            <strong>Interactive figures</strong> — drag, slide and break things. Colours always mean the same thing:{' '}
            <Hl c="blue">inputs</Hl>, <Hl c="green">parameters</Hl>, <Hl c="amber">outputs</Hl>, <Hl c="pink">errors & gradients</Hl>
            , <Hl c="violet">probabilities</Hl>.
          </li>
          <li>
            <strong>A real code editor</strong> — you’ll write TypeScript that runs right here in your browser, with autocomplete
            and instant checks. No installs.
          </li>
          <li>
            <strong>Tiny real models</strong> — everything is trained live on your device. Nothing is faked.
          </li>
        </ul>
        <P>
          Your progress saves automatically, and you can continue on another device with a sync key — no account, ever. Next up:
          your workbench.
        </P>
      </Step>
    </Lesson>
  )
}
